import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { ArrowLeft, Search, BookOpen, MessageCircle, FileText, Video } from "lucide-react";
interface HelpCenterProps {
  onBackToLanding: () => void;
}
const helpCategories = [{
  icon: BookOpen,
  title: "Getting Started",
  description: "Learn the basics of using Decisionr.xyz",
  articles: 12,
  color: "bg-blue-100 text-blue-700"
}, {
  icon: FileText,
  title: "Decision Tracking",
  description: "How to effectively track and analyze your decisions",
  articles: 8,
  color: "bg-green-100 text-green-700"
}, {
  icon: MessageCircle,
  title: "Reviews & Reflection",
  description: "Making the most of scheduled decision reviews",
  articles: 6,
  color: "bg-purple-100 text-purple-700"
}, {
  icon: Video,
  title: "Analytics & Insights",
  description: "Understanding your decision-making patterns",
  articles: 5,
  color: "bg-orange-100 text-orange-700"
}];
export function HelpCenter({
  onBackToLanding
}: HelpCenterProps) {
  return <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={onBackToLanding} className="flex items-center space-x-2">
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Home</span>
              </Button>
              <div className="h-6 w-px bg-border" />
              <div>
                <h1 className="text-2xl font-semibold">Help Center</h1>
                <p className="text-sm text-muted-foreground">
                  Find answers and learn how to use Decisionr.xyz effectively
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-12">
        {/* Hero Section with Search */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-medium mb-4">
            How can we help you?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Search our knowledge base or browse categories to find the answers you need.
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-lg mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search for help articles..." className="pl-10 pr-4 py-3 text-base" />
          </div>
        </div>

        {/* Help Categories */}
        <div className="mb-16">
          <h3 className="text-2xl font-medium mb-8">Browse by Category</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {helpCategories.map((category, index) => <Card key={index} className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader className="text-center">
                  <div className={`w-12 h-12 ${category.color} rounded-lg flex items-center justify-center mx-auto mb-4`}>
                    <category.icon className="h-6 w-6" />
                  </div>
                  
                  <CardDescription>{category.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <Badge variant="secondary">{category.articles} articles</Badge>
                </CardContent>
              </Card>)}
          </div>
        </div>



        {/* Quick Start Guide */}
        <Card className="mb-16 bg-primary/5 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BookOpen className="h-5 w-5" />
              <span>Quick Start Guide</span>
            </CardTitle>
            <CardDescription>
              New to Decisionr.xyz? Follow these steps to get started.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-medium">
                  1
                </div>
                <h4 className="font-medium mb-2">Create Your First Decision</h4>
                <p className="text-sm text-muted-foreground">
                  Click "New Decision" and record a recent choice you made.
                </p>
              </div>
              <div className="text-center">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-medium">
                  2
                </div>
                <h4 className="font-medium mb-2">Set Review Intervals</h4>
                <p className="text-sm text-muted-foreground">
                  Schedule when you want to revisit and evaluate your decision.
                </p>
              </div>
              <div className="text-center">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-medium">
                  3
                </div>
                <h4 className="font-medium mb-2">Track Your Progress</h4>
                <p className="text-sm text-muted-foreground">
                  Use analytics to identify patterns and improve your judgment.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Support */}
        <Card className="text-center">
          <CardContent className="p-8">
            <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-medium mb-2">Still need help?</h3>
            <p className="text-muted-foreground mb-6">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button>Contact Support</Button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16">
        <div className="container py-8">
          <div className="text-center text-sm text-muted-foreground">
            <p>© 2025 Decisionr.xyz. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>;
}